// Safety Overview (2026-09-27) — the Project Overview and the Safety Dashboard as ONE
// page, to replace both. It opens as the Project Overview (no crash data); crash data,
// countermeasures and the statistics join it when loaded (later stages). A source is
// an overview (the server scopes it to the caller's projects) or a whole state.
// Every table sorts, pins and limits its rows; pins are kept per source.
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import '../Safety/SafetyDashboard.css';
import '../Overview/ProjectOverview.css';
import { MultiPicker } from '../Safety/CorridorPicker';
import TopNSelect from '../Safety/TopNSelect';
import OverviewMap from '../Overview/OverviewMap';
import OverviewStrips from '../Overview/OverviewStrips';
import { ClustersTable, CrashClustersTable, SitesTable, SummaryTable } from '../Overview/OverviewTables';
import SettingsDialog, { loadDisabledRules, saveDisabledRules } from '../Safety/SettingsDialog';
import {
  DEFAULT_FILTERS as SAFETY_DEFAULTS, applyCorrelatedOnly, clusterDeficiencies, clusterVisibleCrashes, computeDescriptiveStats, computeSiteTable, filterCrashes, sortClusters,
} from '../Safety/safetyFilters';
import CrashFilters, { DEFAULT_CRASH_FILTERS } from './CrashFilters';
import FiltersDialog from './FiltersDialog';
import NetworkSources from './NetworkSources';
import RoadElementDeleteDialog from './RoadElementDeleteDialog';
import { availablePanels, buildPdfPanels } from './pdfPanels';
import { useServerPermission } from '../../utils/serverPermissions';
import OffendersDialog from './OffendersDialog';
import { clusterLogic, crashLogic, loadWeights, saveWeights, siteLogic, siteScore } from '../Safety/offenders';
import { deleteOverview } from '../Overview/overviewApi';
import { GUARDRAIL_LABEL, NET_CONDITIONS, OBSTRUCTION_ANALYSES, OBSTRUCTION_LABELS, loadRules, rulesHash, rulesParam, saveRules } from './rules';
import { computeImpactSummary, fmtUsd, kabcoOf, visibleFindings } from '../Safety/insightsText';
import { CMF_POLICIES, buildProgram, exposureYears, siteRecommendation } from '../Safety/cmfBenefit';
import CmfCard from '../Safety/CmfCard';
import InsightsPanel from '../Safety/InsightsPanel';
import ClusterInsightsPanel from '../Safety/ClusterInsightsPanel';
import IntersectionsPanel from '../Safety/IntersectionsPanel';
import ProgramPanel from '../Safety/ProgramPanel';
import VerdictCard, { VERDICT_OPTIONS } from '../Safety/VerdictCard';
import { CriteriaDialog, ManagerDialog, Modal, PdfDialog, REPORT_NAME } from '../Overview/OverviewDialogs';
import { CORRELATION_RULES } from '../Safety/safetyFilters';
import { fetchOverviews, mapLimit } from '../Overview/overviewApi';
import { DETAIL_ANALYSES, MAX_DETAIL_ANALYSES } from '../Overview/StationsLayer';
import {
  ANALYSES, ANALYSIS_COLORS, ANALYSIS_NAMES, ROAD_COLORS, SEVERITIES, clusterSites, dedupeSegments, filterSites, numberSegments,
  overviewStats, positionFn, segmentName, stripBlocks, summarize,
} from '../Overview/overviewModel';
import { applySeverity, applySeverityColors, loadSeveritySettings, saveSeveritySettings } from '../Overview/severitySettings';
import {
  fetchAnalysis, fetchCmfLibrary, fetchIntersectionsSO, fetchVerdict, fetchCrashes, fetchStates, loadPins, loadPlaces, savePlaces, parseSource, recallSource, rememberSource, savePins, sourceCorridors, sourceId, sourceSegment,
  sourceStations, deleteRoadElement,
} from './safetyOverviewApi';

const NET_TYPES_KEY = 'safetyOverview:netTypes';     // the road-feature types the map shows
const MAP_TOP_KEY = 'safetyOverview:mapTop';          // how many top sites / clusters the map shows (0 = all)
const MIN_RANGE_MI = 0.01;                              // the shortest milepost range (~53 ft)

/** A path's points within a milepost range, with the point just outside each end, so a short
 *  range (shorter than the path's vertex spacing) still draws — and the map can fit it. */
function pointsInRange(points, [lo, hi]) {
  const idx = points.map((p, i) => [p[2], i]).filter(([mp]) => mp != null);
  const inside = idx.filter(([mp]) => mp >= lo && mp <= hi).map(([, i]) => i);
  const below = idx.filter(([mp]) => mp < lo).sort((a, b) => b[0] - a[0])[0];
  const above = idx.filter(([mp]) => mp > hi).sort((a, b) => a[0] - b[0])[0];
  const keep = new Set([...inside, ...(below ? [below[1]] : []), ...(above ? [above[1]] : [])]);
  return points.filter((_, i) => keep.has(i));
}

const NEAR_MI = 0.1;          // a crash "at" a site: within 0.1 mi of it (the Safety Dashboard's buffer)
const METRIC_LABEL = {
  SSD: 'speed deficit (mph)', PSD: 'speed deficit (mph)', GLR: 'glare minutes / yr', FSA: 'cross slope (%)',
  HCA: 'curve safety score (%)', GRW: 'embankment height (ft)', ISD: 'required ISD (ft)',
};

const SubCard = ({ title, children, defaultOpen = true }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="safety-subcard">
      <button type="button" className="safety-subcard-head" onClick={() => setOpen(!open)} aria-expanded={open}>
        <span className={`safety-section-chevron ${open ? 'open' : ''}`}>▸</span>{title}
      </button>
      {open && <div className="safety-subcard-body">{children}</div>}
    </div>
  );
};

// the defaults Yaron set (2026-09-27): severe deficiencies only
const DEFAULT_SEVERITIES = ['severe'];

// every panel below the map starts collapsed (Yaron, 2026-09-28)
const SectionCard = ({ title, meta, tools, children, defaultOpen = false }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="safety-section">
      <div className="ov-section-head">
        <button type="button" className="safety-section-head ov-section-toggle" onClick={() => setOpen(!open)} aria-expanded={open}>
          <span className={`safety-section-chevron ${open ? 'open' : ''}`}>▸</span>
          <span className="safety-section-title">{title}</span>
          {meta && <span className="ov-section-meta">{meta}</span>}
        </button>
        {open && tools && <div className="ov-section-tools">{tools}</div>}
      </div>
      {open && <div className="safety-section-body">{children}</div>}
    </div>
  );
};

const Seg = ({ value, onChange, options, label }) => (
  <div className="ov-seg" role="group" aria-label={label}>
    {options.map(([v, text]) => (
      <button key={v} type="button" aria-pressed={value === v} onClick={() => onChange(v)}>{text}</button>
    ))}
  </div>
);

/** A milepost typed by hand: digits and one point only, kept within [min, max] (the road, and the other end). */
function MpInput({ label, value, min, max, onChange }) {
  const [text, setText] = useState(value.toFixed(2));
  useEffect(() => { setText(value.toFixed(2)); }, [value]);
  const commit = () => {
    const x = parseFloat(text);
    if (!Number.isFinite(x)) { setText(value.toFixed(2)); return; }
    const v = Math.round(Math.min(Math.max(x, min), max) * 100) / 100;
    setText(v.toFixed(2));
    if (v !== value) onChange(v);
  };
  return (
    <input type="text" inputMode="decimal" className="so-mp-input" aria-label={label} value={text}
      title={`${label}: ${min.toFixed(2)}–${max.toFixed(2)}`}
      onChange={(e) => setText(e.target.value.replace(/[^0-9.]/g, '').replace(/(\..*)\./g, '$1'))}
      onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); commit(); } }}
      onBlur={commit} />
  );
}

// rows of a table: Top N, All, or a number of the user's own (never more than the table holds)
const RowsSelect = ({ id, value, onChange, max }) => (
  <label className="ov-inline" htmlFor={id}>Rows to show
    <TopNSelect id={id} value={value} onChange={onChange} max={max} />
  </label>
);

// opt: the sub line is the first to go when the panels would not fit beside the map
const Tile = ({ label, value, sub, wide, children, opt = false }) => (
  <div className={`ov-tile ${wide ? 'ov-tile-wide' : ''}`}>
    <span className="ov-tile-label">{label}</span>
    {value != null && <span className="ov-tile-value">{value}</span>}
    {sub && <span className={`ov-tile-sub ${opt ? 'so-opt' : ''}`}>{sub}</span>}
    {children}
  </div>
);

export default function SafetyOverview() {
  const [overviews, setOverviews] = useState(null);
  const [states, setStates] = useState([]);
  const [canCreate, setCanCreate] = useState(false);
  const [sourceKey, setSourceKey] = useState(null);      // 'ov:<id>' | 'st:<state>'
  const [listError, setListError] = useState('');
  const [pins, setPins] = useState({ summary: [], clusters: [], sites: [], crashClusters: [], program: [] });
  // crash data: off until asked for; loaded per shown segment and kept while the source stays
  const [crashOn, setCrashOn] = useState(false);
  const [crashData, setCrashData] = useState({});        // segKey -> payload | {error}  (payload._rules = its rule set)
  // user rules (Settings -> Filters): edited live, sent to the server ~0.8 s after the last change
  const [rules, setRules] = useState(() => loadRules());
  const [baseLayer, setBaseLayer] = useState('street');      // the map's base layer: the PDF draws the same one
  const [appliedRules, setAppliedRules] = useState(() => loadRules());
  useEffect(() => {
    const t = setTimeout(() => setAppliedRules(saveRules(rules)), 800);
    return () => clearTimeout(t);
  }, [rules]);
  const rulesQ = useMemo(() => rulesParam(appliedRules), [appliedRules]);
  const rulesTag = useMemo(() => rulesHash(appliedRules), [appliedRules]);
  const rulesRef = useRef(rulesQ);
  rulesRef.current = rulesQ;
  const [corridorList, setCorridorList] = useState([]);
  // worst-offender weights (Settings -> Worst offenders) and remembered map places (per source)
  const [weights, setWeights] = useState(() => loadWeights());
  const [places, setPlaces] = useState([]);
  const [rulesLoad, setRulesLoad] = useState({ total: 0, done: 0 });
  const [crashLoad, setCrashLoad] = useState({ total: 0, done: 0 });
  const [crashFilters, setCrashFilters] = useState({ ...DEFAULT_CRASH_FILTERS });
  const [disabledRules, setDisabledRules] = useState(() => loadDisabledRules());
  const [rulesOpen, setRulesOpen] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(true);
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);
  useEffect(() => {
    if (!menuOpen) return undefined;
    const close = (e) => { if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false); };
    const esc = (e) => { if (e.key === 'Escape') setMenuOpen(false); };
    document.addEventListener('mousedown', close);
    document.addEventListener('keydown', esc);
    return () => { document.removeEventListener('mousedown', close); document.removeEventListener('keydown', esc); };
  }, [menuOpen]);
  const crashToken = useRef(0);
  // stage 4: countermeasures, statistical support, metric profile, milepost range
  const [cmfLibrary, setCmfLibrary] = useState(null);
  const [cmfError, setCmfError] = useState('');
  const [unitCostOverrides, setUnitCostOverrides] = useState({});
  const [verdicts, setVerdicts] = useState({});          // segKey -> {verdict} | {error} | {loading}
  const [verdictShow, setVerdictShow] = useState([]);
  const [strips, setStrips] = useState({});              // `${analysis}|${segKey}` -> {P, N} strips
  const [mpRange, setMpRange] = useState(null);
  const [hiddenRays, setHiddenRays] = useState([]);          // ISD maneuver ids whose rays are off
  // the panels beside the map fit its height: step up the compaction until they do
  const statsRef = useRef(null);
  const [fitLevel, setFitLevel] = useState(0);
  const [loaded, setLoaded] = useState({});            // segKey -> payload
  const [progress, setProgress] = useState({ total: 0, done: 0, failed: [], phase: 'idle' });
  const [filters, setFilters] = useState({ analyses: null, segments: null, severities: [...DEFAULT_SEVERITIES], direction: '' });
  const [view, setView] = useState({ unify: false, axis: 'mp', colourBy: 'analysis', sumBy: 'segment', measure: 'sites', showPaths: true,
    rowsSites: 25, rowsClusters: 10, rowsSummary: 0, rowsCrashClusters: 25, minAnalyses: 2 });
  // sites and clusters open in their rank order: the order the map's Top N takes its head from
  const [sorts, setSorts] = useState({ summary: { key: 'total', dir: 'desc' }, clusters: { key: 'rank', dir: 'asc' },
    sites: { key: 'rank', dir: 'asc' }, crashClusters: { key: 'count', dir: 'desc' } });
  const [focus, setFocus] = useState(null);
  const [stations, setStations] = useState({});          // `${analysis}|${segKey}` -> {P: rows, N: rows}
  const [stationLoad, setStationLoad] = useState({ total: 0, done: 0 });
  const stationToken = useRef(0);
  const [dialog, setDialog] = useState(null);           // 'new' | 'edit' | 'pdf' | 'criteria'
  const [severity, setSeverity] = useState(() => applySeverityColors(loadSeveritySettings()) && loadSeveritySettings());
  const mapRef = useRef(null);
  const loadToken = useRef(0);

  const loadList = useCallback(async (preferKey) => {
    try {
      const [data, st] = await Promise.all([fetchOverviews(), fetchStates().catch(() => ({ states: [] }))]);
      // no demo data on this page: an overview made only of demo projects is left out
      const demo = new Set(st.demoProjects || []);
      data.overviews = (data.overviews || []).filter((o) => !(o.projects.length && o.projects.every((p) => demo.has(p.id))));
      setOverviews(data.overviews);
      setStates(st.states || []);
      setCanCreate(Boolean(data.canCreate));
      setListError('');
      const keys = [...(data.overviews || []).map((o) => sourceId('ov', o.id)), ...(st.states || []).map((x) => sourceId('st', x.id))];
      const wanted = [preferKey, recallSource()].find((k) => k && keys.includes(k));
      setSourceKey(wanted || keys[0] || null);
    } catch (e) {
      setOverviews([]);
      setListError(e.message);
    }
  }, []);
  useEffect(() => { loadList(); }, [loadList]);

  const source = useMemo(() => parseSource(sourceKey), [sourceKey]);
  const overview = useMemo(() => (source?.kind === 'ov' ? (overviews || []).find((o) => o.id === source.id) || null : null),
    [overviews, source]);
  const stateSource = useMemo(() => (source?.kind === 'st' ? states.find((x) => x.id === source.id) || null : null), [states, source]);
  const active = overview || stateSource;                  // the source being shown
  const projectSignature = overview ? overview.projects.map((p) => p.id).join(',') : '';

  // pins are kept per source, across visits (this browser)
  useEffect(() => { if (sourceKey) { setPins(loadPins(sourceKey)); setPlaces(loadPlaces(sourceKey)); } }, [sourceKey]);
  const changePlaces = (fn) => setPlaces((p) => { const next = fn(p); savePlaces(sourceKey, next); return next; });
  const addPlace = useCallback((ll) => changePlaces((p) => [...p, {
    id: `pl:${Date.now()}`, lat: ll.lat, lng: ll.lng, note: '', created: new Date().toISOString(),
  }]), [sourceKey]);   // eslint-disable-line react-hooks/exhaustive-deps
  const updatePlace = useCallback((id, note) => changePlaces((p) => p.map((x) => (x.id === id ? { ...x, note } : x))),
    [sourceKey]);   // eslint-disable-line react-hooks/exhaustive-deps
  const removePlace = useCallback((id) => changePlaces((p) => p.filter((x) => x.id !== id)),
    [sourceKey]);   // eslint-disable-line react-hooks/exhaustive-deps
  const deleteThisOverview = async () => {
    if (!overview || !window.confirm(`Delete the overview "${overview.name}"? Its projects are not affected.`)) return;
    try { await deleteOverview(overview.id); setSourceKey(null); loadList(); } catch (e) { window.alert(`Could not delete: ${e.message}`); }
  };
  const pinsFor = (table) => ({
    pins: pins[table],
    onPins: (next) => setPins((p) => { const all = { ...p, [table]: next }; savePins(sourceKey, all); return all; }),
  });

  // Load: corridors per project, then one payload per corridor, a few at a time.
  useEffect(() => {
    if (!active) { setLoaded({}); return undefined; }
    rememberSource(sourceKey);
    loadToken.current += 1;
    const token = loadToken.current;
    const live = () => loadToken.current === token;
    setLoaded({});
    setStations({});
    setCrashData({});
    setVerdicts({});
    setStrips({});
    setMpRange(null);
    setFocus(null);
    setFilters((f) => ({ ...f, analyses: null, segments: null }));
    setProgress({ total: 0, done: 0, failed: [], phase: 'corridors' });
    (async () => {
      let corridors = [];
      let failed = [];
      try {
        ({ corridors, failed } = await sourceCorridors(source, overview));
      } catch (e) {
        failed = [e.message];
      }
      if (!live()) return;
      setCorridorList(corridors);
      setProgress({ total: corridors.length, done: 0, failed, phase: 'segments' });
      await mapLimit(corridors, 4, async (c) => {
        try {
          const segment = await sourceSegment(source, c, rulesRef.current);
          if (!live()) return;
          setLoaded((prev) => ({ ...prev, [c.key]: segment }));
          setProgress((p) => ({ ...p, done: p.done + 1 }));
        } catch (e) {
          if (live()) setProgress((p) => ({ ...p, done: p.done + 1, failed: [...p.failed, c.key.split('|')[1]] }));
        }
      });
      if (live()) setProgress((p) => ({ ...p, phase: 'ready' }));
    })();
    return () => { loadToken.current += 1; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sourceKey, Boolean(active), projectSignature]);

  const firstRules = useRef(true);
  useEffect(() => {
    if (firstRules.current) { firstRules.current = false; return undefined; }
    if (!active || !corridorList.length) return undefined;
    let live = true;
    setRulesLoad({ total: corridorList.length, done: 0 });
    mapLimit(corridorList, 4, async (c) => {
      try {
        const segment = await sourceSegment(source, c, rulesQ);
        if (live) setLoaded((prev) => ({ ...prev, [c.key]: segment }));
      } catch (e) { /* the previous payload stays */ }
      if (live) setRulesLoad((p) => ({ ...p, done: p.done + 1 }));
    }).then(() => { if (live) setRulesLoad({ total: 0, done: 0 }); });
    return () => { live = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rulesQ]);

  // Part B: a road's road network is fetched in the background on the server; while it is
  // pending the page shows the rest and asks again every 20 s (at most ~15 min)
  const [netPoll, setNetPoll] = useState(0);
  const pendingNet = corridorList.filter((c) => loaded[c.key]?.network?.pending).map((c) => c.key).join(',');
  useEffect(() => {
    if (!active || !pendingNet || netPoll > 45) return undefined;
    let live = true;
    const t = setTimeout(() => {
      const keys = pendingNet.split(',');
      mapLimit(corridorList.filter((c) => keys.includes(c.key)), 4, async (c) => {
        try {
          const segment = await sourceSegment(source, c, rulesQ);
          if (live) setLoaded((prev) => ({ ...prev, [c.key]: segment }));
        } catch (e) { /* asked again next round */ }
      }).then(() => { if (live) setNetPoll((n) => n + 1); });
    }, 20000);
    return () => { live = false; clearTimeout(t); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingNet, netPoll, rulesQ, Boolean(active)]);
  useEffect(() => { setNetPoll(0); }, [rulesQ, sourceKey]);

  const segments = useMemo(() => numberSegments(applySeverity(dedupeSegments(Object.values(loaded)), severity))
    .sort((a, b) => a.road.localeCompare(b.road, undefined, { numeric: true }) || ((a.segment || 0) - (b.segment || 0))
      || ((a.mpMin || 0) - (b.mpMin || 0))), [loaded, severity]);
  const available = useMemo(() => ANALYSES.filter((a) => segments.some((s) => (s.analyses || []).includes(a))), [segments]);
  const analyses = useMemo(() => (filters.analyses ? filters.analyses.filter((a) => available.includes(a)) : available),
    [filters.analyses, available]);
  const segKeys = useMemo(() => (filters.segments ? filters.segments.filter((k) => loaded[k]) : segments.map((s) => s.key)),
    [filters.segments, segments, loaded]);
  const shownSegments = useMemo(() => segments.filter((s) => segKeys.includes(s.key)), [segments, segKeys]);
  // Part B: the road-network features the passing-zone rules used, per shown road, with their source
  const networkFeatures = useMemo(() => shownSegments.flatMap((s) => {
    const net = s.network;
    if (!net || !net.features) return [];
    return net.features.map((f) => {
      const rc = appliedRules.network.conditions[f.type] || {};
      return { ...f, id: `net:${s.key}:${f.type}:${f.mp}:${f.src}`, segName: segmentName(s), state: s.state,
        srcLabel: net.sources?.[f.src]?.label, approachFt: rc.approachFt, departFt: rc.departFt };
    });
  }), [shownSegments, appliedRules]);
  const hasNetwork = shownSegments.some((s) => s.network);
  // deleting a road element: Admins and SuperUsers (DOT, OSM), only Admins (our analyses); the server re-checks
  const canNet = useServerPermission('edit_road_network').allowed;
  const canNetOurs = useServerPermission('edit_road_network_analyses').allowed;
  const canDeleteNetwork = (src) => (src === 'ours' ? canNetOurs : canNet);
  const [deleting, setDeleting] = useState(null);
  const [netEdits, setNetEdits] = useState(0);            // bumps after a delete / restore: the panel re-reads its list
  const refreshState = (stateId) => {
    // every shown road of that state: its road network (and so its rules, crashes, verdicts) changed
    const mine = corridorList.filter((c) => (c.state || String(c.key).split('|')[0]) === stateId);
    return mapLimit(mine, 4, async (c) => {
      try {
        const segment = await sourceSegment(source, c, rulesQ);
        setLoaded((prev) => ({ ...prev, [c.key]: segment }));
      } catch (e) { /* the previous payload stays */ }
    }).then(() => setNetEdits((n) => n + 1));
  };
  // which road-feature types the map draws ([] = all; display only — the rules use every feature)
  const [netTypes, setNetTypesState] = useState(() => {
    try { return JSON.parse(localStorage.getItem(NET_TYPES_KEY) || '[]'); } catch (e) { return []; }
  });
  const setNetTypes = (next) => {
    setNetTypesState(next);
    try { localStorage.setItem(NET_TYPES_KEY, JSON.stringify(next)); } catch (e) { /* private window */ }
  };
  const netTypeOptions = useMemo(() => {
    const counts = {};
    networkFeatures.forEach((f) => { counts[f.type] = (counts[f.type] || 0) + 1; });
    return [...NET_CONDITIONS, ['guardrail', GUARDRAIL_LABEL]].filter(([k]) => counts[k])
      .map(([k, label]) => ({ key: k, label: `${label} · ${counts[k]}` }));
  }, [networkFeatures]);
  // the obstruction types of the loaded roads' deficient SSD stations and short ISD maneuvers, with counts
  const obstructionOptions = useMemo(() => {
    const counts = {};
    shownSegments.forEach((s) => Object.entries(s.obstructions || {}).forEach(([k, n]) => { counts[k] = (counts[k] || 0) + n; }));
    return Object.entries(counts).sort((a, b) => b[1] - a[1])
      .map(([k, n]) => ({ key: k, label: `${OBSTRUCTION_LABELS[k] || k} · ${n}` }));
  }, [shownSegments]);
  const mapNetworkTypes = useMemo(() => (netTypes.length ? networkFeatures.filter((f) => netTypes.includes(f.type)) : networkFeatures),
    [networkFeatures, netTypes]);
  const roadColors = useMemo(() => Object.fromEntries(
    Array.from(new Set(segments.map((s) => s.road))).map((r, i) => [r, ROAD_COLORS[i % ROAD_COLORS.length]])), [segments]);

  const baseSites = useMemo(() => (analyses.length
    ? filterSites(segments, { analyses, segments: segKeys, severities: filters.severities, direction: filters.direction })
    : []), [segments, analyses, segKeys, filters.severities, filters.direction]);

  // ---- crash data (loaded on demand, for the roads shown) ----
  const segKeySignature = segKeys.join(',');
  // crash data and verdicts follow the rules AND the road network they were built on
  const segTag = (s) => `${rulesTag}|${s.network?.stamp || (s.network?.pending ? 'pending' : '')}`;
  const netSignature = shownSegments.map((s) => s.network?.stamp || '').join(',');
  useEffect(() => {
    if (!crashOn || !active) { setCrashLoad({ total: 0, done: 0 }); return undefined; }
    crashToken.current += 1;
    const token = crashToken.current;
    const live = () => crashToken.current === token;
    const missing = shownSegments.filter((s) => !crashData[s.key] || crashData[s.key]._rules !== segTag(s));
    if (!missing.length) { setCrashLoad({ total: 0, done: 0 }); return undefined; }
    setCrashLoad({ total: missing.length, done: 0 });
    mapLimit(missing, 3, async (s) => {
      let payload;
      try { payload = await fetchCrashes(s, rulesQ); } catch (e) { payload = { error: e.message }; }
      if (!live()) return;
      setCrashData((prev) => ({ ...prev, [s.key]: { ...payload, _rules: segTag(s) } }));
      setCrashLoad((p) => ({ ...p, done: p.done + 1 }));
    }).then(() => { if (live()) setCrashLoad({ total: 0, done: 0 }); });
    return () => { crashToken.current += 1; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [crashOn, sourceKey, segKeySignature, segments.length, rulesTag, netSignature]);
  const crashPayloads = useMemo(() => shownSegments.map((s) => [s, crashData[s.key]]).filter(([, p]) => p && !p.error),
    [shownSegments, crashData]);
  const crashFailed = useMemo(() => shownSegments.filter((s) => crashData[s.key]?.error).map((s) => segmentName(s)),
    [shownSegments, crashData]);
  const firstCrashPayload = crashPayloads.length ? crashPayloads[0][1] : null;
  const allCrashes = useMemo(() => (crashOn ? crashPayloads.flatMap(([s, p]) => (p.crashes || []).map((c) => ({
    ...c, crashId: c.id, id: `crash:${s.key}:${c.id}`, lng: c.lon, corridor: s.key, segKey: s.key, road: segmentName(s),
  }))) : []), [crashOn, crashPayloads]);
  const crashAnalysis = analyses.length === 1 ? analyses[0] : 'ALL';
  const asRuns = (list) => list.map((s) => ({ ...s, corridor: s.segKey }));
  // milepost range: offered when the roads shown are ONE road (a milepost means nothing across roads)
  const shownRoads = useMemo(() => Array.from(new Set(shownSegments.map((s) => s.road))), [shownSegments]);
  const roadExtent = useMemo(() => (shownRoads.length === 1 && shownSegments.length
    ? [Math.min(...shownSegments.map((s) => s.mpMin ?? Infinity)), Math.max(...shownSegments.map((s) => s.mpMax ?? -Infinity))] : null),
  [shownRoads, shownSegments]);
  const range = roadExtent && mpRange ? mpRange : null;
  // move one end of the milepost range; the range never gets shorter than MIN_RANGE_MI (equal ends showed nothing)
  const setRangeEnd = (end, v) => {
    const [lo0, hi0] = range || roadExtent;
    const span = Math.min(MIN_RANGE_MI, roadExtent[1] - roadExtent[0]);
    if (end === 0) {
      const lo = Math.max(roadExtent[0], Math.min(v, roadExtent[1] - span));
      setMpRange([lo, Math.max(hi0, lo + span)]);
    } else {
      const hi = Math.min(roadExtent[1], Math.max(v, roadExtent[0] + span));
      setMpRange([Math.min(lo0, hi - span), hi]);
    }
  };
  // the map follows the milepost range: the analysis paths are cut to it, and the map fits it
  const mapSegments = useMemo(() => (!range ? shownSegments : shownSegments.map((s) => ({
    ...s,
    directions: Object.fromEntries(Object.entries(s.directions || {}).map(([dir, d]) => [dir, {
      ...d, points: pointsInRange(d.points || [], range),
    }])),
  }))), [shownSegments, range]);
  const mapNetwork = useMemo(() => (!range ? mapNetworkTypes
    : mapNetworkTypes.filter((f) => f.mp <= range[1] && (f.mpEnd ?? f.mp) >= range[0])), [mapNetworkTypes, range]);
  useEffect(() => { setMpRange(null); }, [shownRoads.join('|')]);   // eslint-disable-line react-hooks/exhaustive-deps
  // statistical support: the analyses whose verdict is "Above chance" on their road
  const assoc = useMemo(() => {
    const out = new Set();
    Object.entries(verdicts).forEach(([k, v]) => {
      Object.entries(v?.verdict?.perAnalysis || {}).forEach(([a, x]) => { if (x.verdict === 'associated') out.add(`${k}|${a}`); });
    });
    return out;
  }, [verdicts]);
  const { sites, crashes } = useMemo(() => {
    let kept = range ? baseSites.filter((s) => s.mpStart != null && (s.mpEnd ?? s.mpStart) >= range[0] && s.mpStart <= range[1]) : baseSites;
    if (!crashOn) return { sites: kept, crashes: null };
    const f = { ...SAFETY_DEFAULTS, ...crashFilters, disabledRules, mpRange: range, dateRange: null,
      direction: filters.direction || 'both', analyses };
    let shown = filterCrashes(allCrashes, f);
    if (crashFilters.aboveChanceOnly) {
      kept = kept.filter((s) => assoc.has(`${s.segKey}|${s.analysis}`));
      shown = shown.map((c) => ({ ...c, findings: Object.fromEntries(Object.entries(c.findings || {})
        .filter(([a]) => assoc.has(`${c.segKey}|${a}`))) })).filter((c) => Object.keys(c.findings).length > 0);
    }
    if (crashFilters.correlatedOnly) {
      const c = applyCorrelatedOnly(asRuns(kept), shown, crashAnalysis);
      shown = c.crashes;
      const ids = new Set(c.runs.map((r) => r.id));
      kept = kept.filter((s) => ids.has(s.id));
    }
    return { sites: kept, crashes: shown };
  }, [crashOn, baseSites, allCrashes, crashFilters, disabledRules, filters.direction, analyses, crashAnalysis, range, assoc]);
  const baseSiteRows = useMemo(() => (crashes ? computeSiteTable(asRuns(sites), crashes) : sites), [sites, crashes]);

  // ---- countermeasures (CMF) ----
  useEffect(() => {
    if (!crashOn || cmfLibrary || cmfError) return;
    fetchCmfLibrary().then((r) => setCmfLibrary(r.library || null)).catch((e) => setCmfError(e.message));
  }, [crashOn, cmfLibrary, cmfError]);
  const costTable = firstCrashPayload?.costTable || null;
  const costs = useMemo(() => costTable?.values || {}, [costTable]);
  const exposure = useMemo(() => exposureYears(firstCrashPayload?.datasetYears, crashFilters.years), [firstCrashPayload, crashFilters.years]);
  const cmfActive = Boolean(crashes && crashFilters.showCmf && cmfLibrary);
  const recommendations = useMemo(() => (cmfActive ? baseSiteRows.map((s) => siteRecommendation(
    { ...s, corridor: s.segKey, mpEnd: s.mpEnd ?? s.mpStart, road: s.segment ? `${s.road} · ${s.segment}` : s.road },
    crashes, cmfLibrary,
    { policy: crashFilters.cmfPolicy, exposure: exposure || 1, costs, ratePct: crashFilters.discountRate, unitCostOverrides },
  )) : []), [cmfActive, baseSiteRows, crashes, cmfLibrary, crashFilters.cmfPolicy, crashFilters.discountRate, exposure, costs, unitCostOverrides]);
  const siteRows = useMemo(() => {
    if (!cmfActive) return baseSiteRows;
    const byId = new Map(recommendations.map((r) => [r.id, r]));
    return baseSiteRows.map((s) => ({ ...s, cmf: byId.get(s.id) }));
  }, [cmfActive, baseSiteRows, recommendations]);
  const program = useMemo(() => (cmfActive ? buildProgram(recommendations) : null), [cmfActive, recommendations]);
  const policyLabel = (CMF_POLICIES.find((p) => p.key === crashFilters.cmfPolicy) || CMF_POLICIES[0]).label;
  const economics = useMemo(() => {
    if (!crashes) return null;
    const correlated = crashes.filter((c) => visibleFindings(c, crashAnalysis).length > 0);
    const s = computeImpactSummary(correlated, costTable);
    return { correlated: s.n, cost: s.cost, lives: s.lives, benefitPerYr: program ? program.totals.benefitPerYr : null };
  }, [crashes, crashAnalysis, costTable, program]);
  const correlatedCount = useMemo(() => (crashes || []).filter((c) => visibleFindings(c, crashAnalysis).length > 0).length,
    [crashes, crashAnalysis]);
  const setUnitCost = (siteKey, crfid, value) => setUnitCostOverrides((o) => ({ ...o, [`${siteKey}:${crfid}`]: value }));

  // ---- statistical support: one verdict per road with crash data ----
  const verdictSignature = crashPayloads.map(([s]) => s.key).join(',');
  useEffect(() => {
    if (!crashOn) return undefined;
    let live = true;
    const missing = crashPayloads.map(([s]) => s).filter((s) => !verdicts[s.key]
      || (verdicts[s.key]._rules !== segTag(s) && !verdicts[s.key].loading));
    if (!missing.length) return undefined;
    setVerdicts((v) => ({ ...v, ...Object.fromEntries(missing.map((s) => [s.key, { ...(v[s.key] || {}), loading: true }])) }));
    mapLimit(missing, 3, async (s) => {
      let out;
      try { out = { verdict: (await fetchVerdict(s, false, rulesQ)).verdict }; } catch (e) { out = { error: e.message }; }
      if (live) setVerdicts((v) => ({ ...v, [s.key]: { ...out, _rules: segTag(s) } }));
    });
    return () => { live = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [crashOn, verdictSignature, rulesTag, netSignature]);
  const recomputeVerdict = (s) => {
    setVerdicts((v) => ({ ...v, [s.key]: { ...(v[s.key] || {}), recomputing: true } }));
    fetchVerdict(s, true, rulesQ).then((r) => setVerdicts((v) => ({ ...v, [s.key]: { verdict: r.verdict, _rules: segTag(s) } })))
      .catch((e) => setVerdicts((v) => ({ ...v, [s.key]: { error: e.message } })));
  };
  const hasVerdicts = Object.values(verdicts).some((v) => v?.verdict);

  // ---- one analysis selected: its value profile along each road (metric lane of the strips) ----
  const metricAnalysis = analyses.length === 1 ? analyses[0] : null;
  const stripSignature = `${metricAnalysis}|${rulesTag}|${shownSegments.map((s) => s.key).join(',')}`;
  useEffect(() => {
    if (!metricAnalysis) return undefined;
    let live = true;
    const missing = shownSegments.filter((s) => (s.analyses || []).includes(metricAnalysis) && !strips[`${metricAnalysis}|${rulesTag}|${s.key}`]);
    if (!missing.length) return undefined;
    mapLimit(missing, 3, async (s) => {
      const pair = {};
      for (const d of Object.keys(s.directions || {})) {
        // eslint-disable-next-line no-await-in-loop
        try { pair[d] = (await fetchAnalysis(s, d, metricAnalysis, rulesQ)).strip || null; } catch (e) { pair[d] = null; }
      }
      if (live) setStrips((x) => ({ ...x, [`${metricAnalysis}|${rulesTag}|${s.key}`]: pair }));
    });
    return () => { live = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stripSignature]);
  const metric = useMemo(() => {
    if (!metricAnalysis) return null;
    const bins = {};
    let binMi = 0.01;
    shownSegments.forEach((s) => {
      const pair = strips[`${metricAnalysis}|${rulesTag}|${s.key}`];
      if (!pair) return;
      const byMp = new Map();
      Object.entries(pair).forEach(([d, strip]) => {
        if (!strip || (filters.direction && d !== filters.direction)) return;
        binMi = strip.binMi || binMi;
        (strip.bins || []).forEach((b) => {
          if (range && (b.mp < range[0] || b.mp > range[1])) return;
          const prev = byMp.get(b.mp);
          if (prev === undefined || Math.abs(b.value) > Math.abs(prev)) byMp.set(b.mp, b.value);   // the worse direction
        });
      });
      bins[s.key] = Array.from(byMp.entries()).sort((a, b) => a[0] - b[0]).map(([mp, value]) => ({ mp, value }));
    });
    return { analysis: metricAnalysis, label: METRIC_LABEL[metricAnalysis] || 'value', binMi, bins };
  }, [metricAnalysis, shownSegments, strips, filters.direction, range, rulesTag]);
  const crashStats = useMemo(() => (crashes
    ? computeDescriptiveStats(crashes, asRuns(sites), null, crashAnalysis) : null), [crashes, sites, crashAnalysis]);
  const crashClusters = useMemo(() => {
    if (!crashes) return [];
    const byId = new Map(crashes.map((c) => [c.crashId, c]));
    return clusterVisibleCrashes(crashes.map((c) => ({ ...c, id: c.crashId }))).map((cl) => {
      const members = cl.crashIds.map((id) => byId.get(id)).filter(Boolean);
      const manners = {};
      members.forEach((m) => { manners[m.manner] = (manners[m.manner] || 0) + 1; });
      const near = sites.filter((s) => s.segKey === cl.corridor && s.mpStart != null
        && cl.mpStart <= (s.mpEnd ?? s.mpStart) + NEAR_MI && cl.mpEnd >= s.mpStart - NEAR_MI);
      const years = members.map((m) => m.year).filter(Boolean);
      return {
        ...cl, id: `ccl:${cl.corridor}|${cl.mpStart}`, segKey: cl.corridor, road: members[0]?.road || cl.road,
        members: [...members].sort((a, b) => a.milepost - b.milepost), nearSites: near,
        mix: { fatal: members.filter((m) => m.severityClass === 'fatal').length,
          injury: members.filter((m) => m.severityClass === 'injury').length,
          pdo: members.filter((m) => m.severityClass === 'pdo').length },
        years: years.length ? (Math.min(...years) === Math.max(...years) ? `${Math.min(...years)}` : `${Math.min(...years)}–${Math.max(...years)}`) : '',
        ki: members.filter((m) => m.severityClass === 'fatal' || m.severityClass === 'injury').length,
        leading: Object.entries(manners).sort((a, b) => b[1] - a[1])[0]?.[0] || '',
        towns: Array.from(new Set(members.map((m) => m.city).filter(Boolean))).join(', '),
        hit: near.length > 0, hitAnalyses: Array.from(new Set(near.map((s) => s.analysis))),
      };
    });
  }, [crashes, sites]);
  const crashesNear = (site) => (crashes || []).filter((c) => c.segKey === site.segKey
    && c.milepost >= site.mpStart - NEAR_MI && c.milepost <= (site.mpEnd ?? site.mpStart) + NEAR_MI);
  const allClusters = useMemo(() => clusterSites(sites), [sites]);
  const clusters = useMemo(() => allClusters.filter((c) => c.n >= view.minAnalyses), [allClusters, view.minAnalyses]);
  const stats = useMemo(() => overviewStats(shownSegments, sites, allClusters, filters.direction),
    [shownSegments, sites, allClusters, filters.direction]);
  const position = useMemo(() => positionFn(shownSegments, view.axis, view.unify), [shownSegments, view.axis, view.unify]);
  const blocks = useMemo(() => stripBlocks(shownSegments, view.unify), [shownSegments, view.unify]);
  const summaryRows = useMemo(() => summarize(shownSegments, sites, view.sumBy, analyses, filters.direction),
    [shownSegments, sites, view.sumBy, analyses, filters.direction]);

  // ONE analysis selected -> the map shows that analysis the way its report does
  // (station dots, glare bands, slope triangles, sight rays). Loaded per corridor, on demand.
  // Up to three analyses keep their report look together; beyond that the map goes back to coloured runs.
  const details = useMemo(() => (analyses.length >= 1 && analyses.length <= MAX_DETAIL_ANALYSES ? analyses : []), [analyses]);
  const stationAnalyses = useMemo(() => details.filter((a) => DETAIL_ANALYSES.includes(a)), [details]);
  const detail = stationAnalyses.join(',');
  const wantsStations = stationAnalyses.length > 0;
  const shownKeySignature = shownSegments.map((s) => s.key).join(',');
  useEffect(() => {
    if (!active || !wantsStations) { setStationLoad({ total: 0, done: 0 }); return undefined; }
    stationToken.current += 1;
    const token = stationToken.current;
    const live = () => stationToken.current === token;
    const missing = stationAnalyses.flatMap((a) => shownSegments
      .filter((s) => (s.analyses || []).includes(a) && !stations[`${a}|${s.key}`]).map((s) => ({ a, s })));
    if (!missing.length) { setStationLoad({ total: 0, done: 0 }); return undefined; }
    setStationLoad({ total: missing.length, done: 0 });
    mapLimit(missing, 4, async ({ a, s }) => {
      let detailOf = { directions: {}, reports: {} };
      try { detailOf = (await sourceStations(source, s, a)) || detailOf; } catch (e) { /* drawn without detail */ }
      if (!live()) return;
      setStations((prev) => ({ ...prev, [`${a}|${s.key}`]: { directions: detailOf.directions || {}, reports: detailOf.reports || {} } }));
      setStationLoad((p) => ({ ...p, done: p.done + 1 }));
    }).then(() => { if (live()) setStationLoad({ total: 0, done: 0 }); });
    return () => { stationToken.current += 1; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sourceKey, detail, wantsStations, shownKeySignature]);
  const stationLayers = useMemo(() => {
    if (!wantsStations) return null;
    // one chunk per corridor and direction: each has its own report page
    return stationAnalyses.map((a) => {
      const chunks = [];
      shownSegments.forEach((s) => {
        const held = stations[`${a}|${s.key}`];
        if (!held) return;
        Object.entries(held.directions).forEach(([d, rows]) => {
          if (filters.direction && d !== filters.direction) return;
          // GRW: the existing guardrails of this side (road network), so shielded warrants draw as such
          const shield = a === 'GRW' && !appliedRules.grwShield.ignoreInventory
            ? ((s.network && s.network.features) || []).filter((f) => f.type === 'guardrail' && (f.side === d || f.side === 'both'))
              .map((f) => [f.mp, f.mpEnd ?? f.mp]) : null;
          // SSD / PSD / GRW: the deficiency runs of this analysis and side under the rules (every severity:
          // the severity filter only hides runs), so a deficient dot outside them draws hollow — too short to count
          const runs = ['SSD', 'PSD', 'GRW'].includes(a)
            ? (s.sites || []).filter((x) => x.analysis === a && x.direction === d && x.mpStart != null)
              .map((x) => [x.mpStart, x.mpEnd ?? x.mpStart]) : undefined;
          chunks.push({ rows, report: held.reports[d] || null, label: `${segmentName(s)} · ${d === 'P' ? 'positive' : 'negative'} direction`, shield, runs });
        });
      });
      return { analysis: a, chunks };
    });
  }, [wantsStations, stationAnalyses, shownSegments, stations, filters.direction, appliedRules.grwShield.ignoreInventory]);

  // ISD rays: switched on and off per intersection and maneuver, from the intersection's popup
  const obstructionKeep = appliedRules.obstructions.length ? appliedRules.obstructions : null;
  const shownStationLayers = useMemo(() => (stationLayers
    ? stationLayers
      // the Obstruction filter narrowed: analyses without an obstruction draw nothing, SSD / ISD only the kept types
      .filter((l) => !obstructionKeep || OBSTRUCTION_ANALYSES.includes(l.analysis))
      .map((l) => ({ ...l, ...(l.analysis === 'ISD' ? { hiddenRays } : {}), ...(obstructionKeep ? { obstructions: obstructionKeep } : {}) }))
    : stationLayers),
  [stationLayers, hiddenRays, obstructionKeep]);
  const isdControl = useMemo(() => ({
    hidden: hiddenRays,
    toggle: (id) => setHiddenRays((h) => (h.includes(id) ? h.filter((x) => x !== id) : [...h, id])),
    setMany: (ids, hide) => setHiddenRays((h) => (hide ? Array.from(new Set([...h, ...ids])) : h.filter((x) => !ids.includes(x)))),
    only: (ids, keep) => setHiddenRays((h) => Array.from(new Set([...h.filter((x) => !ids.includes(x)), ...ids.filter((x) => x !== keep)]))),
    reset: () => setHiddenRays([]),
  }), [hiddenRays]);

  const intersections = useMemo(() => shownSegments.flatMap((s) => (s.intersections || [])
    .map((ix) => ({ ...ix, segKey: s.key, segName: segmentName(s) })))
    .filter((ix) => !range || (ix.mp >= range[0] && ix.mp <= range[1])), [shownSegments, range]);

  // Cluster insights over THE deficiency clusters (the multi-deficiency ones above), with the
  // crashes on each and its best-value countermeasure once crash data is loaded
  const clusterCards = useMemo(() => clusters.map((c) => {
    const runs = c.items.map((s) => ({ ...s, corridor: s.segKey, mpEnd: s.mpEnd ?? s.mpStart, worstSeverity: s.severity }));
    const groups = clusterDeficiencies(runs, crashes || [], { gapMi: NEAR_MI, costs, minRuns: 1 });
    const g = groups.sort((a, b) => b.runCount - a.runCount)[0];
    return g ? { ...g, id: c.id, lat: c.lat, lng: c.lng, road: segmentName(c) } : null;
  }).filter(Boolean), [clusters, crashes, costs]);

  // ---- one rank for the map and the panels: the map's Top N is the head of the same ranked list,
  // after every filter (so "Top 10 on the map" = the first 10 rows of the sites table / cluster insights)
  const [mapTop, setMapTopState] = useState(() => {
    try { return { sites: 0, clusters: 0, ...JSON.parse(localStorage.getItem(MAP_TOP_KEY) || '{}') }; } catch (e) { return { sites: 0, clusters: 0 }; }
  });
  const setMapTop = (patch) => setMapTopState((m) => {
    const next = { ...m, ...patch };
    try { localStorage.setItem(MAP_TOP_KEY, JSON.stringify(next)); } catch (e) { /* private window */ }
    return next;
  });
  const rankedSites = useMemo(() => {
    const bySeg = new Map();
    (crashes || []).forEach((c) => { if (!bySeg.has(c.segKey)) bySeg.set(c.segKey, []); bySeg.get(c.segKey).push(c); });
    const scored = sites.map((s) => {
      let cost = 0;
      let correlated = 0;
      (bySeg.get(s.segKey) || []).forEach((c) => {
        if (c.milepost >= s.mpStart - NEAR_MI && c.milepost <= (s.mpEnd ?? s.mpStart) + NEAR_MI) {
          cost += costs[kabcoOf(c)] || 0;
          if ((c.findings || {})[s.analysis]) correlated += 1;
        }
      });
      return [s, siteScore(s, { cost, correlated }, weights)];
    });
    return scored.sort((a, b) => b[1] - a[1] || (b[0].lengthMi || 0) - (a[0].lengthMi || 0) || (a[0].mpStart ?? 0) - (b[0].mpStart ?? 0))
      .map(([s]) => s);
  }, [sites, crashes, costs, weights]);
  const siteRank = useMemo(() => new Map(rankedSites.map((s, i) => [s.id, i + 1])), [rankedSites]);
  const rankedClusters = useMemo(() => {
    const order = sortClusters(clusterCards, 'worst', weights).map((c) => c.id);
    const byId = new Map(clusters.map((c) => [c.id, c]));
    const seen = new Set(order);
    return [...order.map((id) => byId.get(id)).filter(Boolean),
      ...clusters.filter((c) => !seen.has(c.id)).sort((a, b) => (a.mpStart ?? 0) - (b.mpStart ?? 0))];
  }, [clusters, clusterCards, weights]);
  const clusterRank = useMemo(() => new Map(rankedClusters.map((c, i) => [c.id, i + 1])), [rankedClusters]);
  // the map: the top N of each (pinned rows always too), or everything
  const topOf = (ranked, n, pinnedIds) => {
    if (!n) return ranked;
    const head = ranked.slice(0, n);
    const ids = new Set(head.map((x) => x.id));
    return [...head, ...ranked.filter((x) => pinnedIds.includes(x.id) && !ids.has(x.id))];
  };
  const mapSites = useMemo(() => topOf(rankedSites, mapTop.sites, pins.sites || []), [rankedSites, mapTop.sites, pins.sites]); // eslint-disable-line react-hooks/exhaustive-deps
  const mapClusters = useMemo(() => topOf(rankedClusters, mapTop.clusters, pins.clusters || []), [rankedClusters, mapTop.clusters, pins.clusters]); // eslint-disable-line react-hooks/exhaustive-deps
  const onMapSites = useMemo(() => (mapTop.sites ? new Set(mapSites.map((s) => s.id)) : null), [mapTop.sites, mapSites]);
  const onMapClusters = useMemo(() => (mapTop.clusters ? new Set(mapClusters.map((c) => c.id)) : null), [mapTop.clusters, mapClusters]);
  const topNav = {
    sites: { value: mapTop.sites, onChange: (n) => setMapTop({ sites: n }), max: rankedSites.length, rank: siteRank,
      list: mapSites, pinned: pins.sites || [], title: siteLogic(weights) },
    clusters: { value: mapTop.clusters, onChange: (n) => setMapTop({ clusters: n }), max: rankedClusters.length, rank: clusterRank,
      list: mapClusters, pinned: pins.clusters || [], title: clusterLogic(weights) },
  };
  const shownStates = useMemo(() => Array.from(new Set(shownSegments.map((s) => s.state))), [shownSegments]);
  const [ixState, setIxState] = useState('');
  const intersectionState = shownStates.includes(ixState) ? ixState : shownStates[0] || '';

  // the Safety panels (Insights, Cluster insights, Program, Intersections) speak the Safety
  // Dashboard's focus: {crashId} | {bounds, lat, lng} | {mpStart, analysis, direction, corridor} | {lat, lng}
  const focusFromPanel = (f, opts) => {
    if (!f) return;
    if (f.crashId != null) {
      const c = (crashes || []).find((x) => x.id === f.crashId);
      showOnMap(c || { id: f.crashId, lat: f.lat, lng: f.lng }, opts);
      return;
    }
    if (f.lat == null && f.mpStart != null) {
      showOnMap(sites.find((s) => s.segKey === f.corridor && s.analysis === f.analysis && s.direction === f.direction
        && s.mpStart === f.mpStart), opts);
      return;
    }
    showOnMap({ id: f.key, lat: f.lat, lng: f.lng }, opts);
  };

  // opts.scroll === false: move the map to the item without bringing the map into view
  const showOnMap = useCallback((item, opts) => {
    if (!item || item.lat == null) return;
    setFocus({ id: item.id, lat: item.lat, lng: item.lng, stamp: Date.now() });
    if (mapRef.current && !(opts && opts.scroll === false)) mapRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, []);

  const patchView = (p) => setView((v) => ({ ...v, ...p }));
  const loading = progress.phase === 'corridors' || progress.phase === 'segments';
  const setCorrelationRules = (next) => { saveDisabledRules(next); setDisabledRules(next); };
  const maxBar = Math.max(1, ...stats.byAnalysis.map((b) => b.sites));

  // Beside the map: the panels must stay inside the map's height. Measure after every change
  // of what they show and, if they spill over, step up: 1 compact type, 2 no optional sub
  // lines, 3 no sub lines at all (the numbers always stay).
  const fitKey = [crashStats ? 1 : 0, economics ? 1 : 0, analyses.join(','), stats.sites, crashStats?.nCrashes].join('|');
  useLayoutEffect(() => { setFitLevel(0); }, [fitKey]);
  useLayoutEffect(() => {
    const el = statsRef.current;
    if (!el || fitLevel >= 3) return;
    if (el.scrollHeight > el.clientHeight + 1) setFitLevel((n) => n + 1);
  });

  if (overviews === null) return <div className="safety-dashboard ov-dashboard"><div className="safety-placeholder">Loading…</div></div>;

  return (
    <div className="safety-dashboard ov-dashboard so-dashboard"
      style={{ '--sev-severe': severity.colors.severe, '--sev-moderate': severity.colors.moderate, '--sev-minor': severity.colors.minor }}>
      <header className="safety-header">
        <div>
          <h1>Safety Overview</h1>
          <p className="safety-muted">Every road of an order or of a state on one map, with its deficiencies, clusters and reports.</p>
        </div>
        <div className="ov-header-tools">
          <label className="safety-filter" htmlFor="so-pick"><span>Show</span>
            <select id="so-pick" value={sourceKey || ''} onChange={(e) => setSourceKey(e.target.value || null)}
              disabled={!overviews.length && !states.length}>
              {!overviews.length && !states.length && <option value="">Nothing to show yet</option>}
              {overviews.length > 0 && (
                <optgroup label="Overviews">
                  {overviews.map((o) => <option key={o.id} value={sourceId('ov', o.id)}>{o.name} ({o.projects.length} project{o.projects.length === 1 ? '' : 's'})</option>)}
                </optgroup>
              )}
              {states.length > 0 && (
                <optgroup label="Whole states">
                  {states.map((x) => <option key={x.id} value={sourceId('st', x.id)}>All collections in {x.label}</option>)}
                </optgroup>
              )}
            </select>
          </label>
          {overview?.canEdit && <button type="button" className="safety-btn safety-btn-ghost" onClick={() => setDialog('edit')}>Edit</button>}
          {overview?.canEdit && <button type="button" className="safety-btn safety-btn-ghost" onClick={deleteThisOverview}>Delete</button>}
          {canCreate && <button type="button" className="safety-btn safety-btn-ghost" onClick={() => setDialog('new')}>New overview</button>}
          <button type="button" className={`safety-btn ${crashOn ? 'safety-btn-ghost' : ''}`} disabled={!segments.length}
            onClick={() => setCrashOn((on) => !on)} aria-pressed={crashOn}
            title={crashOn ? 'Back to the deficiencies only' : 'Load the crash data of the roads shown: crash layers, filters, correlation'}>
            {crashOn ? 'Unload crash data' : 'Load crash data'}
          </button>
          <div className="so-settings" ref={menuRef}>
            <button type="button" className="safety-btn safety-btn-ghost so-gear" aria-haspopup="menu" aria-expanded={menuOpen}
              onClick={() => setMenuOpen((o) => !o)} title="Criteria and severity, correlation rules, filters">
              <i className="fa-solid fa-gear" aria-hidden="true" /> Settings
              {disabledRules.length > 0 && <span className="so-badge" title={`${disabledRules.length} correlation rule(s) off`}>{disabledRules.length}</span>}
            </button>
            {menuOpen && (
              <div className="so-settings-menu" role="menu">
                <button type="button" role="menuitem" onClick={() => { setMenuOpen(false); setDialog('criteria'); }}>
                  <span>Criteria and severity</span><small>what counts as a deficiency, its severe / moderate cuts and colours</small>
                </button>
                <button type="button" role="menuitem" onClick={() => { setMenuOpen(false); setRulesOpen(true); }}>
                  <span>Correlation rules</span>
                  <small>how a crash is tied to each analysis · {CORRELATION_RULES.length - disabledRules.length} of {CORRELATION_RULES.length} on</small>
                </button>
                <button type="button" role="menuitem" onClick={() => { setMenuOpen(false); setDialog('filters'); }}>
                  <span>Filters</span><small>what counts as a deficiency: run lengths, guardrail gaps, passing-zone rules</small>
                </button>
                <button type="button" role="menuitem" onClick={() => { setMenuOpen(false); setDialog('offenders'); }}>
                  <span>Worst offenders</span><small>how the insights rank crashes and clusters</small>
                </button>
              </div>
            )}
          </div>
          <button type="button" className="safety-btn ov-primary" disabled={!overview || loading || !segments.length}
            hidden={!overview}
            onClick={() => setDialog('pdf')} title={`Export the ${REPORT_NAME} as a PDF`}>Export PDF report</button>
        </div>
      </header>

      {listError && <div className="safety-error">Could not load the overviews: {listError}</div>}
      {!overviews.length && !states.length && !listError && (
        <div className="safety-placeholder">
          {canCreate
            ? 'No overview yet. Create one from the RSA projects of an order with "New overview".'
            : 'No overview includes a project you are assigned to yet. An administrator or your Super User can create one.'}
        </div>
      )}

      {active && (
        <>
          {overview?.description && <p className="safety-muted ov-description">{overview.description}</p>}
          {(loading || progress.failed.length > 0) && (
            <div className="safety-note" role="status">
              {progress.phase === 'corridors' && (overview
                ? `Finding the analysed roads of ${overview.projects.length} project${overview.projects.length === 1 ? '' : 's'}…`
                : `Finding the analysed roads of ${stateSource.label}…`)}
              {progress.phase === 'segments' && `Loading ${progress.done} of ${progress.total} segments… The first load of a road reads its analyses and can take a minute; later loads are instant.`}
              {progress.phase === 'ready' && progress.failed.length > 0 && `Could not load: ${progress.failed.join(', ')}.`}
            </div>
          )}
          {pendingNet && (
            <div className="safety-note" role="status">
              Reading the road network (MassDOT, OpenStreetMap) for {pendingNet.split(',').length} road{pendingNet.split(',').length === 1 ? '' : 's'} in
              the background. The passing-zone rules near signals, intersections, crosswalks and the other SOP features
              apply to {pendingNet.split(',').length === 1 ? 'it' : 'them'} as soon as it arrives; everything else is already shown.
            </div>
          )}
          {stationLoad.total > 0 && (
            <div className="safety-note" role="status">
              Loading the {stationAnalyses.join(', ')} stations: {stationLoad.done} of {stationLoad.total} done.
              The first load reads every station of a road and can take a minute.
            </div>
          )}
          {rulesLoad.total > 0 && (
            <div className="safety-note" role="status">
              Applying the new rules: {rulesLoad.done} of {rulesLoad.total} roads. The previous results stay on screen until then.
            </div>
          )}
          {crashOn && crashLoad.total > 0 && (
            <div className="safety-note" role="status">
              Loading crash data: {crashLoad.done} of {crashLoad.total} roads. The first load of a road matches its crashes to
              every deficiency and can take half a minute; later loads are instant.
            </div>
          )}
          {crashOn && crashFailed.length > 0 && (
            <div className="safety-note" role="status">No crash data for: {crashFailed.join(', ')}.</div>
          )}
          {progress.phase === 'ready' && !segments.length && (
            <div className="safety-placeholder">No analysed road with a surveyed centreline was found {overview ? 'in these projects' : 'in this state'}.</div>
          )}

          {segments.length > 0 && (
            <>
              <div className="so-filters">
              <button type="button" className="so-filters-head" aria-expanded={filtersOpen} onClick={() => setFiltersOpen((o) => !o)}
                title={filtersOpen ? 'Collapse the filters' : 'Show the filters'}>
                <span className={`safety-section-chevron ${filtersOpen ? 'open' : ''}`}>▸</span>
                <span className="so-filters-title">Filters</span>
                {!filtersOpen && (
                  <span className="so-filters-summary">
                    {analyses.length} of {available.length} analyses · {segKeys.length} of {segments.length} segments
                    {' · '}{filters.severities.length === SEVERITIES.length ? 'every severity' : filters.severities.join(' + ')}
                    {' · '}{filters.direction === 'P' ? 'positive direction' : filters.direction === 'N' ? 'negative direction' : 'both directions'}
                    {crashOn ? ` · crashes: ${crashFilters.severities.length ? crashFilters.severities.join(' + ') : 'every severity'}${crashFilters.correlatedOnly ? ', correlated only' : ''}` : ''}
                    {range ? ` · MP ${range[0].toFixed(2)}–${range[1].toFixed(2)}` : ''}
                  </span>
                )}
              </button>
              {filtersOpen && (
              <>
              <div className="safety-filterbar">
                <MultiPicker tall label="Analyses" noun="analyses" options={available.map((a) => ({ key: a, label: `${a} · ${ANALYSIS_NAMES[a]}` }))}
                  selected={analyses} onChange={(next) => setFilters((f) => ({ ...f, analyses: next }))}
                  extraToggle={{ id: 'ov-show-paths', label: 'Show the analysis paths on the map', checked: view.showPaths, onChange: (v) => patchView({ showPaths: v }) }}
                  title="Up to three analyses are drawn with their own report signs; four or more as coloured runs" />
                <MultiPicker tall label="Roads and segments" noun="segments" wide
                  options={segments.map((s) => ({ key: s.key, label: `${segmentName(s)} · MP ${(s.mpMin || 0).toFixed(1)}–${(s.mpMax || 0).toFixed(1)} · ${s.projectId ? `#${s.projectId}` : s.route}` }))}
                  selected={segKeys} onChange={(next) => setFilters((f) => ({ ...f, segments: next }))} />
                <MultiPicker tall label="Severity" noun="severities" options={SEVERITIES.map((s) => ({ key: s, label: s }))}
                  selected={filters.severities} onChange={(next) => setFilters((f) => ({ ...f, severities: next }))} />
                {obstructionOptions.length > 0 && (
                  <MultiPicker tall allMeansEmpty label="Obstruction" noun="obstructions" options={obstructionOptions}
                    selected={rules.obstructions} onChange={(next) => setRules((r) => ({ ...r, obstructions: next }))}
                    title="What blocks the sight line (SSD stations, ISD maneuvers). Narrowed: only those deficiencies stay, and the analyses without an obstruction (PSD, GLR, FSA, GRW, HCA) are hidden — map, tables, correlation, verdicts and the PDF alike." />
                )}
                {netTypeOptions.length > 0 && (
                  <MultiPicker tall allMeansEmpty label="Road features" noun="road features" options={netTypeOptions}
                    selected={netTypes} onChange={setNetTypes}
                    title="Which road features (SOP passing-zone conditions) the map shows. Display only: the passing-zone rules still use every feature." />
                )}
                <label className="safety-filter" htmlFor="ov-dir"><span>Direction</span>
                  <select id="ov-dir" value={filters.direction} onChange={(e) => setFilters((f) => ({ ...f, direction: e.target.value }))}>
                    <option value="">Both</option><option value="P">Positive</option><option value="N">Negative</option>
                  </select>
                </label>
                <div className="safety-filter"><span>Segments</span>
                  <Seg label="Segments" value={view.unify} onChange={(v) => patchView({ unify: v })}
                    options={[[false, 'Separately'], [true, 'Unified per road']]} />
                </div>
                <div className="safety-filter"><span>Position as</span>
                  <Seg label="Position as" value={view.axis} onChange={(v) => patchView({ axis: v })}
                    options={[['mp', 'Milepost'], ['mi', 'Mileage']]} />
                </div>
                <div className="safety-filter"><span>Colour sites by</span>
                  <Seg label="Colour sites by" value={view.colourBy} onChange={(v) => patchView({ colourBy: v })}
                    options={[['analysis', 'Analysis'], ['severity', 'Severity']]} />
                </div>
              </div>
              {crashOn && (
                <CrashFilters crashes={allCrashes} datasetYears={firstCrashPayload?.datasetYears} value={crashFilters}
                  onChange={setCrashFilters} disabledCount={disabledRules.length} hasVerdicts={hasVerdicts} />
              )}
              {roadExtent && roadExtent[1] > roadExtent[0] && (
                <div className="safety-filterbar so-range" aria-label="Milepost range">
                  <span className="so-crash-label so-range-label">Mileposts</span>
                  <input type="range" aria-label="From milepost" min={roadExtent[0]} max={roadExtent[1]} step={0.01}
                    value={(range || roadExtent)[0]}
                    onChange={(e) => setRangeEnd(0, Number(e.target.value))} />
                  <input type="range" aria-label="To milepost" min={roadExtent[0]} max={roadExtent[1]} step={0.01}
                    value={(range || roadExtent)[1]}
                    onChange={(e) => setRangeEnd(1, Number(e.target.value))} />
                  <span className="ov-inline">MP
                    <MpInput label="From milepost" value={(range || roadExtent)[0]} min={roadExtent[0]}
                      max={Math.max(roadExtent[0], (range || roadExtent)[1] - MIN_RANGE_MI)} onChange={(lo) => setRangeEnd(0, lo)} />
                    –
                    <MpInput label="To milepost" value={(range || roadExtent)[1]}
                      min={Math.min(roadExtent[1], (range || roadExtent)[0] + MIN_RANGE_MI)} max={roadExtent[1]} onChange={(hi) => setRangeEnd(1, hi)} />
                  </span>
                  {range && <button type="button" className="safety-btn safety-btn-ghost" onClick={() => setMpRange(null)}>Whole road</button>}
                </div>
              )}
              </>
              )}
              </div>

              <div className="ov-main" ref={mapRef}>
                <div className="ov-map-pane">
                  <OverviewMap segments={mapSegments} sites={mapSites} clusters={mapClusters} topNav={topNav} intersections={intersections} analyses={analyses}
                    colourBy={view.colourBy} roadColors={roadColors} focus={focus} details={details} stationLayers={shownStationLayers}
                    showPaths={view.showPaths} direction={filters.direction}
                    crashes={crashes} crashClusters={crashClusters} crashAnalysis={crashAnalysis} recommendations={recommendations}
                    isd={isdControl} places={places} onAddPlace={addPlace} onUpdatePlace={updatePlace} onRemovePlace={removePlace}
                    network={mapNetwork} onBaseLayer={setBaseLayer}
                    canDeleteNetwork={canDeleteNetwork} onDeleteNetwork={setDeleting} grwShield={!appliedRules.grwShield.ignoreInventory} />
                </div>
                {/* Right of the map: the panels that fit its (constant) height. With crash data the
                    crash panels take the place of "Densest road" and ISD gets its compact form. */}
                <div ref={statsRef} className={`ov-stats ${crashStats ? 'so-stats-crash' : ''} ${fitLevel ? `so-fit-${fitLevel}` : ''}`}>
                  <Tile label={filters.direction ? `Surveyed · ${filters.direction === 'P' ? 'positive' : 'negative'} direction` : 'Surveyed road'}
                    value={`${stats.miles.toFixed(1)} mi`}
                    opt sub={`${filters.direction ? '' : `${stats.directionalMi.toFixed(1)} mi counting both directions · `}${stats.segments} segment${stats.segments === 1 ? '' : 's'} · ${stats.roads} road${stats.roads === 1 ? '' : 's'}${overview ? ` · ${stats.projects} project${stats.projects === 1 ? '' : 's'}` : ''}`} />
                  <Tile label="Deficiency sites" value={stats.sites} sub={<><b>{stats.severe} severe</b></>} />
                  <Tile label="Deficient length" value={`${stats.deficientMi.toFixed(1)} mi`} opt sub={<><b>{stats.pct.toFixed(1)}%</b> of the {stats.directionalMi.toFixed(1)} directional miles analysed</>} />
                  <Tile label="Multi-deficiency clusters" value={stats.clusters} sub={`${stats.clusters3} with 3+ analyses`} />
                  {crashStats && (
                    <>
                      <Tile label="Crashes" value={crashStats.nCrashes} opt
                        sub={<>{stats.miles ? `${(crashStats.nCrashes / stats.miles).toFixed(2)} per road mile · ` : ''}{firstCrashPayload?.datasetVersion || ''}</>} />
                      <Tile label="Fatal · injury · property damage"
                        value={`${crashStats.bySeverity.fatal} · ${crashStats.bySeverity.injury} · ${crashStats.bySeverity.pdo}`}
                        opt sub={crashStats.nAnimal ? `${crashStats.nAnimal} animal strike${crashStats.nAnimal === 1 ? '' : 's'}` : null} />
                      <Tile label={`Near a deficiency site (${NEAR_MI} mi)`} value={crashStats.nearShare != null ? `${crashStats.nearShare}%` : '—'}
                        sub={<><b>{crashStats.crashesNearRuns}</b> of {crashStats.nCrashes} crashes · {crashStats.crashesInsideRuns} inside a site</>} />
                      <Tile label="Crash clusters" value={crashClusters.length}
                        sub={`${crashClusters.filter((c) => !c.hit).length} with no deficiency site near`} />
                      {economics && (economics.benefitPerYr
                        ? <Tile wide label="Expected savings / yr" value={fmtUsd(economics.benefitPerYr)}
                          opt sub={`countermeasure program · ${policyLabel}`} />
                        : <Tile wide label="Social cost at deficiencies" value={fmtUsd(economics.cost)}
                          sub={`${economics.correlated} correlated crash${economics.correlated === 1 ? '' : 'es'}${economics.lives ? ` · ${economics.lives} li${economics.lives === 1 ? 'fe' : 'ves'}` : ''}`} />)}
                    </>
                  )}
                  {analyses.includes('ISD') && crashStats && (
                    <Tile wide label="ISD · intersections with a deficiency" value={`${stats.intersectionsShort} of ${stats.intersections}`}
                      sub={`${stats.maneuversShort} of ${stats.maneuvers} maneuvers short of the required sight distance`} />
                  )}
                  {analyses.includes('ISD') && !crashStats && (
                    <Tile wide label="ISD · intersections analysed" value={stats.intersections}
                      sub={stats.intersections
                        ? <><b>{stats.intersectionsShort} with a deficiency</b> · {stats.maneuvers} maneuver{stats.maneuvers === 1 ? '' : 's'} analysed, {stats.maneuversShort} short of the required sight distance</>
                        : 'No intersection analysis in this selection'} />
                  )}
                  {!crashStats && (
                    <Tile wide label="Densest road" value={stats.densest ? stats.densest.road : '—'}
                      sub={stats.densest ? <><b>{stats.densest.rate.toFixed(2)} sites per mile</b></> : null} />
                  )}
                  <Tile wide label={`By analysis · sites · deficient miles${analyses.includes('ISD') ? ' (ISD above)' : ''}`}>
                    <div className="ov-bars">
                      {stats.byAnalysis.filter((b) => analyses.includes(b.analysis)).map((b) => (
                        <div className="ov-bar" key={b.analysis} title={ANALYSIS_NAMES[b.analysis]}>
                          <span>{b.analysis}</span>
                          <span className="ov-bar-track"><span className="ov-bar-fill" style={{ width: `${(b.sites / maxBar) * 100}%`, background: ANALYSIS_COLORS[b.analysis] }} /></span>
                          <span className="ov-bar-n">{b.sites} · {b.miles.toFixed(1)} mi</span>
                        </div>
                      ))}
                      {!stats.byAnalysis.some((b) => analyses.includes(b.analysis)) && <span className="ov-tile-sub">{analyses.length ? 'ISD is reported above' : 'No analysis selected'}</span>}
                    </div>
                  </Tile>
                </div>
              </div>

              <div className={`safety-insights-grid ${crashes ? '' : 'single'}`}>
                <SectionCard title="Cluster insights" meta={`${clusterCards.length} multi-deficiency cluster${clusterCards.length === 1 ? '' : 's'} (places where 2+ analyses overlap), one by one`}>
                  <ClusterInsightsPanel clusters={clusterCards} onFocus={focusFromPanel} recommendations={recommendations}
                    analysis={crashAnalysis} hasCrashData={Boolean(crashes)} crashNoun={crashFilters.excludeAnimal ? 'non-animal crash' : 'crash'}
                    defaultSort="worst" weights={weights} sortTitle={clusterLogic(weights)} />
                </SectionCard>
                {crashes && (
                  <SectionCard title="Insights" meta={`${correlatedCount} correlated crash${correlatedCount === 1 ? '' : 'es'} (a deficiency at the crash location), crash by crash`}>
                    <InsightsPanel crashes={crashes} road={shownRoads.length === 1 ? shownRoads[0] : ''} analysis={crashAnalysis}
                      onFocus={focusFromPanel} costTable={costTable} program={program} policyLabel={policyLabel}
                      stats={crashStats} crashNoun={crashFilters.excludeAnimal ? 'non-animal crash' : 'crash'}
                      defaultSort="worst" weights={weights} sortTitle={crashLogic(weights)} />
                  </SectionCard>
                )}
              </div>

              <SectionCard title="Milepost strip" meta={`${blocks.length} ${view.unify ? 'road' : 'segment'}${blocks.length === 1 ? '' : 's'} · click a site or an intersection to find it on the map, double-click to open its report`}>
                <OverviewStrips blocks={blocks} sites={sites} clusters={clusters} analyses={analyses}
                  colourBy={view.colourBy} axis={view.axis} onFocus={showOnMap} crashes={crashes} metric={metric} />
              </SectionCard>

              <SectionCard title="Multi-deficiency clusters"
                meta={`${clusters.length} place${clusters.length === 1 ? '' : 's'} where ${view.minAnalyses}+ analyses coincide`}
                tools={(
                  <>
                    <label className="ov-inline" htmlFor="ov-min-an">At least
                      <select id="ov-min-an" className="ov-select" value={view.minAnalyses} onChange={(e) => patchView({ minAnalyses: Number(e.target.value) })}>
                        <option value={2}>2 analyses</option><option value={3}>3 analyses</option><option value={4}>4 analyses</option>
                      </select>
                    </label>
                    <RowsSelect id="ov-rows-clusters" value={view.rowsClusters} onChange={(n) => patchView({ rowsClusters: n })}
                      max={clusters.filter((c) => c.n >= view.minAnalyses).length} />
                  </>
                )}>
                <ClustersTable rows={clusters} position={position} axis={view.axis} unify={view.unify} limit={view.rowsClusters}
                  rank={clusterRank} onMap={onMapClusters} rankTitle={clusterLogic(weights)}
                  sort={sorts.clusters} onSort={(s) => setSorts((x) => ({ ...x, clusters: s }))} onRowClick={showOnMap} {...pinsFor('clusters')} />
              </SectionCard>

              {crashes && (
                <SectionCard title="Crash clusters"
                  meta={`${crashClusters.length} place${crashClusters.length === 1 ? '' : 's'} with 2+ crashes within 0.3 mi · "none" = no deficiency site near it`}
                  tools={<RowsSelect id="so-rows-crash-clusters" value={view.rowsCrashClusters} onChange={(n) => patchView({ rowsCrashClusters: n })} max={crashClusters.length} />}>
                  <CrashClustersTable rows={crashClusters} limit={view.rowsCrashClusters}
                    sort={sorts.crashClusters} onSort={(s) => setSorts((x) => ({ ...x, crashClusters: s }))}
                    onRowClick={(cl) => showOnMap(cl)} {...pinsFor('crashClusters')} />
                </SectionCard>
              )}

              <SectionCard title="Deficiency summary" meta={`${summaryRows.length} ${view.sumBy === 'road' ? 'road' : 'segment'}${summaryRows.length === 1 ? '' : 's'}`}
                tools={(
                  <>
                    <span className="ov-inline">Rows <Seg label="Summary rows" value={view.sumBy} onChange={(v) => patchView({ sumBy: v })} options={[['road', 'Roads'], ['segment', 'Segments']]} /></span>
                    <span className="ov-inline">Show <Seg label="Summary measure" value={view.measure} onChange={(v) => patchView({ measure: v })} options={[['sites', 'Sites'], ['miles', 'Deficient miles']]} /></span>
                    <RowsSelect id="so-rows-summary" value={view.rowsSummary} onChange={(n) => patchView({ rowsSummary: n })} max={summaryRows.length} />
                  </>
                )}>
                <SummaryTable rows={summaryRows} analyses={analyses} measure={view.measure} by={view.sumBy}
                  sort={sorts.summary} onSort={(s) => setSorts((x) => ({ ...x, summary: s }))} limit={view.rowsSummary}
                  onRowClick={(r) => showOnMap(r.firstSite)} {...pinsFor('summary')} />
              </SectionCard>

              <SectionCard title="Deficiency sites" meta={`${sites.length} site${sites.length === 1 ? '' : 's'}`}
                tools={<RowsSelect id="ov-rows-sites" value={view.rowsSites} onChange={(n) => patchView({ rowsSites: n })} max={siteRows.length} />}>
                <SitesTable rows={siteRows} position={position} axis={view.axis} limit={view.rowsSites} crashMode={Boolean(crashes)}
                  rank={siteRank} onMap={onMapSites} rankTitle={siteLogic(weights)}
                  sort={sorts.sites} onSort={(s) => setSorts((x) => ({ ...x, sites: s }))} onRowClick={showOnMap} {...pinsFor('sites')}
                  expand={crashes ? (site) => {
                    const near = crashesNear(site);
                    const card = site.cmf ? <CmfCard rec={site.cmf} exposure={exposure} ratePct={crashFilters.discountRate} /> : null;
                    if (!near.length) return <>{card}<span className="safety-muted">No crash within {NEAR_MI} mi of this site under the current crash filters.</span></>;
                    return (
                      <>
                      {card}
                      <ul className="so-crash-list">
                        {near.map((c) => (
                          <li key={c.id}>
                            <button type="button" className="ov-report" onClick={() => showOnMap(c)}>MP {c.milepost.toFixed(3)}</button>
                            {' '}{c.severity || c.severityClass} · {c.manner || 'crash'} · {c.datetime ? c.datetime.slice(0, 10) : ''}
                            {Object.keys(c.findings || {}).length ? ` · findings: ${Object.keys(c.findings).join(', ')}` : ''}
                          </li>
                        ))}
                      </ul>
                      </>
                    );
                  } : undefined} />
              </SectionCard>

              {cmfActive && (
                <SectionCard title="Countermeasure program"
                  meta={`ranked · ${costTable?.source || 'crash costs'} · FHWA CMF Clearinghouse`}>
                  <ProgramPanel recommendations={recommendations} exposure={exposure} ratePct={crashFilters.discountRate}
                    costSource={costTable?.source} policyLabel={policyLabel}
                    corridorLabel={overview ? overview.name : (stateSource?.label || 'selection')}
                    onUnitCost={setUnitCost} onFocus={focusFromPanel} {...pinsFor('program')} />
                </SectionCard>
              )}
              {crashes && crashFilters.showCmf && cmfError && (
                <div className="safety-note">Countermeasure library unavailable ({cmfError}); countermeasure columns are hidden.</div>
              )}

              {crashes && (
                <SectionCard title="Statistical support" meta="per road · does the crash pattern follow the deficiencies more than chance would?">
                  <div className="safety-insights-nav safety-verdict-tools">
                    <MultiPicker label="Show verdicts" options={VERDICT_OPTIONS} selected={verdictShow} onChange={setVerdictShow}
                      noun="verdicts" allMeansEmpty title="Show only analyses whose verdict is one of these outcomes" />
                  </div>
                  {crashPayloads.map(([s]) => {
                    const v = verdicts[s.key] || {};
                    const card = (
                      <VerdictCard verdict={v.verdict} loading={Boolean(v.loading)} error={v.error} show={verdictShow}
                        onRecompute={() => recomputeVerdict(s)} recomputing={Boolean(v.recomputing)} />
                    );
                    return crashPayloads.length > 1
                      ? <SubCard key={s.key} defaultOpen={false} title={`${segmentName(s)} · MP ${(s.mpMin || 0).toFixed(1)}–${(s.mpMax || 0).toFixed(1)}`}>{card}</SubCard>
                      : <React.Fragment key={s.key}>{card}</React.Fragment>;
                  })}
                </SectionCard>
              )}

              {crashes && intersectionState && (
                <SectionCard title="Intersection priorities" meta="district-wide, from the crash snapshot"
                  tools={shownStates.length > 1 ? (
                    <label className="ov-inline" htmlFor="so-ix-state">State
                      <select id="so-ix-state" className="ov-select" value={intersectionState} onChange={(e) => setIxState(e.target.value)}>
                        {shownStates.map((st) => <option key={st} value={st}>{st}</option>)}
                      </select>
                    </label>
                  ) : null}>
                  <IntersectionsPanel stateId={intersectionState} onFocus={focusFromPanel} library={cmfLibrary}
                    policy={crashFilters.cmfPolicy} datasetYears={firstCrashPayload?.datasetYears}
                    showCmf={Boolean(cmfLibrary && crashFilters.showCmf)} fetchIntersections={fetchIntersectionsSO} />
                </SectionCard>
              )}

              {hasNetwork && (
                <SectionCard title="Road network sources"
                  meta={`${networkFeatures.length} road feature${networkFeatures.length === 1 ? '' : 's'} used by the passing-zone rules · what was taken from where`}>
                  <NetworkSources segments={shownSegments} rules={appliedRules} edits={netEdits}
                    canDelete={canDeleteNetwork} onRestored={refreshState} />
                </SectionCard>
              )}
            </>
          )}
        </>
      )}

      {(dialog === 'new' || dialog === 'edit') && (
        <ManagerDialog overview={dialog === 'edit' ? overview : null} onClose={() => setDialog(null)}
          onSaved={(o) => { setDialog(null); loadList(sourceId('ov', o.id)); }}
          onDeleted={() => { setDialog(null); setSourceKey(null); loadList(); }} />
      )}
      <SettingsDialog open={rulesOpen} onClose={() => setRulesOpen(false)} disabledRules={disabledRules} onChange={setCorrelationRules} />
      {dialog === 'offenders' && (
        <OffendersDialog weights={weights} onChange={(w) => setWeights(saveWeights(w))} onClose={() => setDialog(null)} />
      )}
      {deleting && (
        <RoadElementDeleteDialog feature={deleting} onClose={() => setDeleting(null)}
          onConfirm={async (reason) => {
            await deleteRoadElement(deleting.state, deleting, deleting.segName, reason);
            refreshState(deleting.state);
          }} />
      )}
      {dialog === 'filters' && (
        <FiltersDialog rules={rules} onChange={setRules} onClose={() => setDialog(null)} />
      )}
      {dialog === 'criteria' && (
        <CriteriaDialog settings={severity} onClose={() => setDialog(null)}
          onChange={(next) => { const saved = saveSeveritySettings(next); applySeverityColors(saved); setSeverity(saved); }} />
      )}
      {dialog === 'pdf' && overview && (
        <PdfDialog overview={overview} onClose={() => setDialog(null)} basemap={baseLayer}
          panels={(() => {
            const verdictRows = crashPayloads.map(([s]) => [`${segmentName(s)} · MP ${(s.mpMin || 0).toFixed(1)}–${(s.mpMax || 0).toFixed(1)}`, verdicts[s.key]]);
            return {
              available: availablePanels({ clusterCards, crashes, program, verdictRows }),
              build: (limit) => buildPdfPanels({
                clusterCards, crashes, crashAnalysis, costTable, recommendations, policyLabel, exposure,
                ratePct: crashFilters.discountRate, weights, verdictRows, limit,
                crashNoun: crashFilters.excludeAnimal ? 'non-animal crash' : 'crash',
              }),
            };
          })()}
          appendixData={{
            correlation: CORRELATION_RULES.map((r) => ({ title: r.title, rule: r.rule, on: !disabledRules.includes(r.key) })),
            offenders: { crash: crashLogic(weights), cluster: clusterLogic(weights) },
            cmf: {
              policy: policyLabel, discountRate: crashFilters.discountRate,
              exposureYears: crashOn ? exposure : null,
              datasetYears: firstCrashPayload?.datasetYears ? `${Math.min(...firstCrashPayload.datasetYears)}–${Math.max(...firstCrashPayload.datasetYears)}` : null,
              costs: costTable ? { name: costTable.name, source: costTable.source, values: costTable.values } : null,
            },
          }}
          counts={{ sites: sites.length, clusters: clusters.length, segments: shownSegments.length }}
          request={{
            filters: { analyses, segments: segKeys, severities: filters.severities, direction: filters.direction },
            options: { axis: view.axis, unify: view.unify, rowsSites: view.rowsSites, rowsClusters: view.rowsClusters,
              minAnalyses: view.minAnalyses, sortSites: sorts.sites, sortClusters: sorts.clusters, severity, rules: appliedRules,
              // the rank (tables' "rank" sort) and what the map shows (its Top N), so the PDF matches the page
              siteOrder: rankedSites.map((x) => x.id), clusterOrder: rankedClusters.map((x) => x.id),
              mapSites: mapTop.sites ? mapSites.map((x) => x.id) : null, mapClusters: mapTop.clusters ? mapClusters.map((x) => x.id) : null },
          }} />
      )}
    </div>
  );
}
